import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, GitCompareArrows, X, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_CATEGORIES = ['all', 'gold', 'silver', 'platinum', 'copper'];
const MAX_COMPARE = 3;

function formatWeight(product) {
  const w = product.weight;
  const u = product.weight_unit || 'oz';
  if (u === 'kg') return w === 1 ? '1 kg' : `${w} kg`;
  if (u === 'g') return `${w} g`;
  if (u === 'oz_avdp') return `${Number.isInteger(w) ? w : parseFloat(w.toFixed(4))} oz avdp`;
  // oz: trim trailing zeros for clean display (e.g. 1.000 -> 1)
  const display = Number.isInteger(w) ? w : parseFloat(w.toFixed(4));
  return `${display} oz`;
}

function CompareBar({ items, onRemove, onCompare, onClear }) {
  if (!items.length) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0A] border-t border-[#D4AF37]/30 shadow-2xl" data-testid="compare-bar">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0 overflow-x-auto">
          <GitCompareArrows size={18} className="text-[#D4AF37] flex-shrink-0" />
          <span className="text-sm text-gray-400 flex-shrink-0" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {items.length}/{MAX_COMPARE}
          </span>
          {items.map(p => (
            <div key={p.id} className="flex items-center gap-2 bg-gray-800 rounded-sm px-3 py-1.5 flex-shrink-0">
              {p.image_url && <img src={p.image_url} alt="" className="w-6 h-6 object-cover rounded-sm" />}
              <span className="text-xs text-white truncate max-w-[120px]" style={{ fontFamily: "'Poppins', sans-serif" }}>{p.name}</span>
              <button onClick={() => onRemove(p.id)} className="text-gray-500 hover:text-red-400" data-testid={`compare-remove-${p.id}`}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={onClear} className="text-xs text-gray-500 hover:text-white transition-colors" data-testid="compare-clear">
            Clear
          </button>
          <Button onClick={onCompare} disabled={items.length < 2} className="btn-gold text-sm px-6" data-testid="compare-btn">
            Compare {items.length < 2 ? `(${2 - items.length} more)` : `(${items.length})`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CompareDialog({ items, open, onClose, onQuote }) {
  if (!items.length) return null;

  const rows = [
    { label: 'Image', render: p => p.image_url ? <img src={p.image_url} alt={p.name} className="w-20 h-20 object-cover rounded-sm mx-auto" /> : <div className="w-20 h-20 bg-gray-100 rounded-sm mx-auto" /> },
    { label: 'Category', render: p => <span className="uppercase text-xs font-semibold text-[#D4AF37]">{p.category}</span> },
    { label: 'Mint', render: p => p.mint || '-' },
    { label: 'Weight', render: p => formatWeight(p) },
    { label: 'Purity', render: p => p.purity ? `${p.purity} fine` : '-' },
    { label: 'Premium', render: p => `${p.premium_percentage}%` },
    { label: 'Price', render: p => p.pricing_mode === 'fixed' && p.fixed_price_nzd ? <span className="font-bold">NZ${p.fixed_price_nzd.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}</span> : <span className="text-gray-400 italic">Quote</span> },
    { label: 'Description', render: p => <span className="text-xs leading-relaxed">{p.description}</span> },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="compare-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle style={{ fontFamily: "'Poppins', sans-serif" }}>
            <div className="flex items-center gap-2">
              <GitCompareArrows size={20} className="text-[#D4AF37]" />
              Compare Products ({items.length})
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="compare-table">
            <thead>
              <tr className="bg-[#0A0A0A]">
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3 w-28 sticky left-0 bg-[#0A0A0A] z-10" style={{ fontFamily: "'Poppins', sans-serif" }}></th>
                {items.map(p => (
                  <th key={p.id} className="text-center px-4 py-3 min-w-[180px]">
                    <p className="text-sm font-semibold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>{p.name}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.label} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className={`text-xs font-medium text-gray-500 px-4 py-3 sticky left-0 z-10 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {row.label}
                  </td>
                  {items.map(p => (
                    <td key={p.id} className="text-center text-sm px-4 py-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {row.render(p)}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="bg-white border-t">
                <td className="text-xs font-medium text-gray-500 px-4 py-4 sticky left-0 bg-white z-10" style={{ fontFamily: "'Poppins', sans-serif" }}>Action</td>
                {items.map(p => (
                  <td key={p.id} className="text-center px-4 py-4">
                    <Button onClick={() => { onClose(); onQuote(p); }} className="btn-gold text-xs px-4" data-testid={`compare-quote-${p.id}`}>
                      Request Quote
                    </Button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [quoteProduct, setQuoteProduct] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const [compareIds, setCompareIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products`).then(r => {
      setProducts(r.data);
      const pid = searchParams.get('product');
      if (pid) {
        const p = r.data.find(x => x.id === pid);
        if (p) setQuoteProduct(p);
      }
    }).catch(err => {
      console.error('Failed to load products:', err);
    });
  }, [searchParams]);

  useEffect(() => {
    axios.get(`${API}/categories`).then(r => {
      if (Array.isArray(r.data) && r.data.length) {
        setCategories(['all', ...r.data.map(c => c.slug)]);
      }
    }).catch(err => {
      console.error('Failed to load categories:', err);
    });
  }, []);

  const filtered = products.filter(p => {
    if (filter !== 'all' && p.category !== filter) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const toggleCompare = (product) => {
    setCompareIds(prev => {
      if (prev.includes(product.id)) return prev.filter(id => id !== product.id);
      if (prev.length >= MAX_COMPARE) { toast.error(`You can compare up to ${MAX_COMPARE} products`); return prev; }
      return [...prev, product.id];
    });
  };

  const compareItems = products.filter(p => compareIds.includes(p.id));

  const handleQuote = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, product_id: quoteProduct?.id, product_name: quoteProduct?.name, quote_type: 'buy' });
      toast.success('Quote request submitted! We will be in touch shortly.');
      setQuoteProduct(null);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit quote');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="products-page" className={compareIds.length ? 'pb-20' : ''}>
      <SEO
        title="Buy Gold, Silver & Platinum Bullion"
        description="Browse investment-grade gold, silver and platinum bullion coins, bars and rounds from Perth Mint, Royal Canadian Mint, and more. Request a quote for NZD pricing."
        path="/products"
      />
      <section className="bg-[#0A0A0A] py-16 text-center">
        <div className="max-w-7xl mx-auto px-6">
          <p className="section-label mb-3">Our Products</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: "Cinzel, serif" }}>Buy Bullion</h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Browse our range of investment-grade gold, silver, platinum, and copper products. All prices are indicative and based on current NZD spot prices.
          </p>
        </div>
      </section>

      <section className="py-12 max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 h-12 bg-white" data-testid="product-search" />
          </div>
          <div className="flex gap-2">
            {categories.map(c => (
              <button key={c} onClick={() => setFilter(c)} className={`px-4 py-2 text-sm font-medium rounded-sm capitalize transition-all ${filter === c ? 'bg-[#0A0A0A] text-[#D4AF37]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={{ fontFamily: "'Poppins', sans-serif" }} data-testid={`filter-${c}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
          {filtered.map(product => {
            const isSelected = compareIds.includes(product.id);
            return (
              <div key={product.id} className={`group bg-white border border-gray-200 rounded-md overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300 flex flex-col h-full ${isSelected ? 'ring-2 ring-[#D4AF37] border-transparent' : ''}`} data-testid={`product-${product.id}`}>
                <div className="aspect-square overflow-hidden bg-gray-50 relative p-5 sm:p-6">
                  <img src={product.image_url} alt={product.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                  <button
                    onClick={() => toggleCompare(product)}
                    className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 focus-visible:opacity-100 ${isSelected ? 'opacity-100 bg-[#D4AF37] text-[#0A0A0A]' : 'bg-black/40 text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A]'}`}
                    title={isSelected ? 'Remove from comparison' : 'Add to compare'}
                    data-testid={`compare-toggle-${product.id}`}
                  >
                    {isSelected ? <Check size={12} strokeWidth={3} /> : <GitCompareArrows size={11} />}
                  </button>
                  {product.purity && (
                    <span className="absolute bottom-2 left-2 text-[9px] uppercase tracking-wider text-white font-medium bg-black/55 px-1.5 py-0.5 rounded" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {product.purity} fine
                    </span>
                  )}
                </div>
                <div className="p-3 flex flex-col flex-1 border-t border-gray-100">
                  <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-semibold" style={{ fontFamily: "'Poppins', sans-serif" }}>{product.category}</span>
                  <h3 className="text-sm font-semibold mt-0.5 mb-1 leading-tight line-clamp-2 min-h-[2.5rem]" style={{ fontFamily: "'Poppins', sans-serif" }}>{product.name}</h3>
                  {product.mint && (
                    <p className="text-[10px] text-gray-400 mb-2 line-clamp-1" style={{ fontFamily: "'Poppins', sans-serif" }}>{product.mint} &middot; {formatWeight(product)}</p>
                  )}
                  {product.pricing_mode === 'fixed' && product.fixed_price_nzd && (
                    <p className="text-base font-bold text-[#0A0A0A] mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      NZ${product.fixed_price_nzd.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}
                    </p>
                  )}
                  <div className="mt-auto pt-2">
                    {product.pricing_mode === 'fixed' && product.fixed_price_nzd ? (
                      <Button onClick={() => setQuoteProduct(product)} className="btn-gold w-full text-xs h-9" data-testid={`buy-now-btn-${product.id}`}>
                        Buy Now
                      </Button>
                    ) : (
                      <Button onClick={() => setQuoteProduct(product)} className="btn-gold w-full text-xs h-9" data-testid={`request-quote-btn-${product.id}`}>
                        Request Quote
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-gray-500 py-12">No products found matching your criteria.</p>
        )}
      </section>

      <CompareBar
        items={compareItems}
        onRemove={(id) => setCompareIds(prev => prev.filter(x => x !== id))}
        onCompare={() => setCompareOpen(true)}
        onClear={() => setCompareIds([])}
      />

      <CompareDialog
        items={compareItems}
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        onQuote={(p) => setQuoteProduct(p)}
      />

      <Dialog open={!!quoteProduct} onOpenChange={() => setQuoteProduct(null)}>
        <DialogContent className="max-w-md" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Poppins', sans-serif" }}>
              {quoteProduct?.pricing_mode === 'fixed' ? 'Confirm Purchase' : 'Request a Quote'}
            </DialogTitle>
          </DialogHeader>
          {quoteProduct && (
            <div className="mb-4 p-3 bg-gray-50 rounded-sm">
              <p className="font-semibold text-sm">{quoteProduct.name}</p>
              {quoteProduct.mint && <p className="text-xs text-gray-400">{quoteProduct.mint}</p>}
              {quoteProduct.pricing_mode === 'fixed' && quoteProduct.fixed_price_nzd && (
                <p className="text-lg font-bold text-[#0A0A0A] mt-1">NZ${quoteProduct.fixed_price_nzd.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}</p>
              )}
            </div>
          )}
          <form onSubmit={handleQuote} className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="bg-white" data-testid="quote-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className="bg-white" data-testid="quote-email" />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required className="bg-white" data-testid="quote-phone" />
            </div>
            <div>
              <Label>Additional Information</Label>
              <Textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={3} className="bg-white" data-testid="quote-message" />
            </div>
            <Button type="submit" disabled={submitting} className="btn-gold w-full" data-testid="submit-quote-btn">
              {submitting ? 'Submitting...' : quoteProduct?.pricing_mode === 'fixed' ? 'Confirm Order' : 'Submit Quote Request'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
